import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Upload, FileText, Image, File, Trash2, Download, 
  Cloud, Shield, Lock, CheckCircle, Clock, FolderOpen,
  AlertCircle, Eye, MoreVertical, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DocumentStorage = ({ subjectId }) => {
  const [documents, setDocuments] = useState([
    { id: '1', name: 'Consent_Form_Signed.pdf', type: 'pdf', size: '245 KB', uploadedAt: '2025-12-15', uploadedBy: 'Dr. Smith', encrypted: true },
    { id: '2', name: 'Lab_Results_Dec2025.pdf', type: 'pdf', size: '1.2 MB', uploadedAt: '2025-12-14', uploadedBy: 'Lab Tech', encrypted: true },
    { id: '3', name: 'Medical_History.docx', type: 'docx', size: '89 KB', uploadedAt: '2025-12-10', uploadedBy: 'Nurse Jones', encrypted: true },
    { id: '4', name: 'ECG_Scan.png', type: 'image', size: '3.4 MB', uploadedAt: '2025-12-08', uploadedBy: 'Dr. Chen', encrypted: true },
    { id: '5', name: 'Insurance_Card.jpg', type: 'image', size: '156 KB', uploadedAt: '2025-12-05', uploadedBy: 'Admin', encrypted: true },
  ]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback((files) => {
    // Simulate upload
    setUploading(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploading(false);
          
          // Add new document to list
          const newDoc = {
            id: String(Date.now()),
            name: files[0].name,
            type: files[0].type.includes('pdf') ? 'pdf' : files[0].type.includes('image') ? 'image' : 'doc',
            size: `${(files[0].size / 1024).toFixed(1)} KB`,
            uploadedAt: new Date().toISOString().split('T')[0],
            uploadedBy: 'Current User',
            encrypted: true,
          };
          setDocuments(prev => [newDoc, ...prev]);
          toast.success('Document uploaded successfully');
          return 0;
        }
        return prev + 10;
      });
    }, 200);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'image':
        return <Image className="w-5 h-5 text-blue-500" />;
      default:
        return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const filteredDocuments = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSize = documents.reduce((acc, doc) => {
    const size = parseFloat(doc.size);
    const unit = doc.size.includes('MB') ? 1024 : 1;
    return acc + (size * unit);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Security Badges */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <Shield className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-green-700">HIPAA Compliant</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Lock className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-700">AES-256 Encrypted</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
          <Clock className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-700">Versioned</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
          <Cloud className="w-4 h-4 text-orange-600" />
          <span className="text-sm font-medium text-orange-700">AWS S3</span>
        </div>
      </div>

      {/* Storage Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
                <p className="text-xs text-gray-500">Total Files</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{documents.filter(d => d.encrypted).length}</p>
                <p className="text-xs text-gray-500">Encrypted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Cloud className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{(totalSize / 1024).toFixed(1)} MB</p>
                <p className="text-xs text-gray-500">Storage Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Zone */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-[#1877F2]" />
            Upload Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragActive 
                ? 'border-[#1877F2] bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                  <Cloud className="w-8 h-8 text-[#1877F2]" />
                </div>
                <p className="text-gray-600">Uploading...</p>
                <div className="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#1877F2] h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500">{uploadProgress}%</p>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-2">
                  Drag and drop files here, or{' '}
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[#1877F2] font-medium hover:underline"
                  >
                    browse
                  </button>
                </p>
                <p className="text-xs text-gray-400">
                  Supports PDF, DOC, DOCX, PNG, JPG up to 25MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => e.target.files && handleFiles(e.target.files)}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium text-gray-700 flex items-center">
              <FolderOpen className="w-5 h-5 mr-2 text-[#1877F2]" />
              Uploaded Documents
            </CardTitle>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No documents found</p>
              </div>
            ) : (
              filteredDocuments.map((doc) => (
                <div 
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg border flex items-center justify-center">
                      {getFileIcon(doc.type)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{doc.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{doc.size}</span>
                        <span>•</span>
                        <span>{doc.uploadedAt}</span>
                        <span>•</span>
                        <span>{doc.uploadedBy}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.encrypted && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs text-green-700">
                        <Lock className="w-3 h-3" />
                        Encrypted
                      </div>
                    )}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Eye className="w-4 h-4 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Download className="w-4 h-4 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* AWS S3 Configuration Notice */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">AWS S3 Configuration Required</p>
          <p className="text-sm text-amber-700 mt-1">
            To enable cloud storage, please provide your AWS credentials (IAM Access Key, Secret Key, and S3 Bucket name) 
            in the system settings. Documents are currently stored locally.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentStorage;
